import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { FileDropzone } from "../components/shared/FileDropzone";
import { Instructions } from "../components/shared/Instructions";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../components/utils";
import { Input, Label } from "../components/shared/InputField";
import {
  useDownloadFactorTemplate,
  useUploadCustomFactor,
} from "../queries/allocationMatrix";
import {
  AllocationFactor,
  useAllocationMatrixStore,
} from "../store/useAllocationMatrixStore";

interface CreateFactorDialogProps {
  open: boolean;
  onClose: () => void;
  onFactorCreated: (factor: AllocationFactor) => void;
  existingNames?: string[];
}

export default function CreateFactorDialog({
  open,
  onClose,
  onFactorCreated,
  existingNames = [],
}: CreateFactorDialogProps) {
  const store = useAllocationMatrixStore();
  const [factorName, setFactorName] = useState(store.pendingFactorName);
  const [file, setFile] = useState<File | undefined>();
  const [nameError, setNameError] = useState("");

  const { mutate: downloadTemplate, isLoading: downloading } =
    useDownloadFactorTemplate();
  const { mutate: uploadFactor, isLoading: uploading } =
    useUploadCustomFactor();

  useEffect(() => {
    if (open) {
      setFactorName(store.pendingFactorName);
      setFile(undefined);
      setNameError("");
    }
  }, [open]);

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setFactorName(value);
    store.setPendingFactorName(value);
    setNameError("");
  }

  function handleClose() {
    if (uploading || downloading) return;
    onClose();
  }

  function handleDownloadTemplate() {
    if (!factorName.trim()) {
      setNameError("Enter a factor name before downloading the template.");
      return;
    }
    downloadTemplate(factorName.trim(), {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${factorName.trim().replace(/\s+/g, "_")}_template.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        toast.error("Failed to download template. Please try again.");
      },
    });
  }

  function handleSave() {
    if (!factorName.trim()) {
      setNameError("Factor name is required.");
      return;
    }
    const trimmed = factorName.trim().toLowerCase();
    if (existingNames.some((n) => n.toLowerCase() === trimmed)) {
      setNameError(
        "A factor with this name already exists. Choose a different name to avoid overwriting data."
      );
      return;
    }
    if (!file) {
      toast.error("Please upload the filled template before saving.");
      return;
    }
    uploadFactor(
      { factorName: factorName.trim(), file },
      {
        onSuccess: (factor) => {
          store.setPendingFactorName("");
          onFactorCreated(factor);
          onClose();
        },
        onError: () => {
          toast.error("Failed to save factor. Please try again.");
        },
      }
    );
  }

  const isBusy = uploading || downloading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <div className="bg-white rounded-lg w-[640px]">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b">
          <VStack className="gap-0.5">
            <h6 className="text-lg font-semibold text-black-501">
              Create New Factor
            </h6>
            <p className="text-xs text-gray-401">
              Name your factor, download the template, fill it in and upload it
              back.
            </p>
          </VStack>
          <span
            onClick={handleClose}
            className="cursor-pointer text-gray-500 hover:text-gray-700 ml-4 flex-shrink-0"
          >
            <Icon icon="material-symbols:close" width={20} />
          </span>
        </div>

        {/* Body */}
        <VStack className="gap-5 px-6 py-6">
          {/* Factor name */}
          <VStack className="gap-1.5">
            <Label>
              Factor Name <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="e.g. Performance Score"
              value={factorName}
              onChange={handleNameChange}
              disabled={isBusy}
            />
            {nameError && <p className="text-xs text-red-500">{nameError}</p>}
          </VStack>

          <div className="border-t" />

          {/* Download template */}
          <HStack className="items-start justify-between gap-6">
            <VStack className="gap-1">
              <p className="text-sm font-semibold text-black-501">
                Download template
              </p>
              <p className="text-xs text-gray-401">
                Contains HRID, Employee Name, and a column for your factor. Fill
                in a value for every employee.
              </p>
            </VStack>
            <ButtonPrimary1
              onClick={handleDownloadTemplate}
              disabled={isBusy}
              loading={downloading}
              className="flex-shrink-0"
            >
              <Icon icon="ri:download-2-line" height={15} className="mr-1.5" />
              Download
            </ButtonPrimary1>
          </HStack>

          <div className="border-t" />

          {/* Upload */}
          <VStack className="gap-2">
            <p className="text-sm font-semibold text-black-501">
              Upload filled template
            </p>
            <FileDropzone
              onFileSelect={setFile}
              onClear={() => setFile(undefined)}
              selectedFile={file}
              acceptedFileExtensions={[".xlsx", ".xls"]}
              instructionText="Excel files only (.xlsx, .xls)"
              disabled={isBusy}
            />
          </VStack>

          <Instructions
            data={[
              "Factor name is saved automatically — close this dialog to fill the template and come back.",
              "Do not modify the HRID or Employee Name columns in the template.",
            ]}
          />
        </VStack>

        {/* Footer */}
        <HStack className="justify-between gap-3 px-6 py-4 border-t">
          <ButtonPrimary1 onClick={handleClose} disabled={isBusy}>
            Cancel
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={handleSave}
            disabled={!file || !factorName.trim() || isBusy}
            loading={uploading}
          >
            Save Factor
          </ButtonPrimary>
        </HStack>
      </div>
    </Dialog>
  );
}
